import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { useBranches } from '@/common/hooks/queries/useBranches'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { ClientCompanyBranchData } from '@/common/types/client/companyBranch.type'
import { Option } from '@/common/types/option'
import { billingSupplyAuthorizationColumns } from '@/features/supplyAuthorizations/constants/billingSupplyAuthorizationColumns.constant'
import { billingSupplyAuthorizationStatusOptions } from '@/features/supplyAuthorizations/constants/billingSupplyAuthorizationStatus.constant'
import { useBillingSupplyAuthorizations } from '@/features/supplyAuthorizations/hooks/useBillingSupplyAuthorizations'
import { BillingSupplyAuthorizationTransformer } from '@/features/supplyAuthorizations/services/billingSupplyAuthorizationTransformer'
import {
  BillingSupplyAuthorizationListData,
  BillingSupplyAuthorizationTableItem,
} from '@/features/supplyAuthorizations/types/billingSupplyAuthorization.type'
import { FileStackIcon, SearchIcon } from 'lucide-react'

const SupplyAuthorizationsList = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const { date, formattedDate } = useFilterDate(filterParams)

  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchBranch, setSearchBranch] = useDebounce('')

  const {
    data: billingSupplyAuthorizations,
    meta,
    isFetching: billingSupplyAuthorizationsIsFetching,
    error: billingSupplyAuthorizationsError = {} as any,
    refetch: refetchBillingSupplyAuthorizations,
  } = useBillingSupplyAuthorizations({
    filters: {
      search,
      page: filterParams.page,
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      status: filterParams.status?.value,
      branchId: filterParams.branch?.value,
    },
    mapper: Mapper.mapWithTransformer<BillingSupplyAuthorizationListData[], BillingSupplyAuthorizationTableItem[]>({
      transformer: BillingSupplyAuthorizationTransformer.tableTransformer(),
    }),
  })

  const { data: branchOptions } = useBranches({
    filters: {
      search: searchBranch,
    },
    mapper: Mapper.mapToOptions<ClientCompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<FileStackIcon className="size-10" strokeWidth={1} />}
        title="AF (Autorização de fornecimento)"
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={billingSupplyAuthorizationsError?.status}
        refetchFunction={refetchBillingSupplyAuthorizations}
      >
        <div className="flex flex-col gap-6">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value)
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />

          <div className="flex items-center gap-2">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Status',
                  options: [{ label: 'Todas', value: '' }, ...billingSupplyAuthorizationStatusOptions],
                  onChange: (e: any) =>
                    setFilterParams((prev) => ({
                      ...prev,
                      status: e.value ? { value: e.value, label: e.label } : null,
                    })),
                  value: filterParams.status,
                },
                {
                  className: 'min-w-40',
                  placeholder: 'Filial',
                  options: [{ label: 'Todas', value: '' }, ...((branchOptions || []) as Option[])],
                  onChange: (e: any) =>
                    setFilterParams((prev) => ({
                      ...prev,
                      branch: e.value ? { value: e.value, label: e.label } : null,
                    })),
                  onInputChange: (value) => setSearchBranch(value),
                  value: filterParams.branch,
                },
              ]}
            />
          </div>

          <Table
            className="max-h-[calc(100vh-260px)]"
            rows={billingSupplyAuthorizations || []}
            columns={billingSupplyAuthorizationColumns}
            isLoadingData={billingSupplyAuthorizationsIsFetching}
            page={meta.current_page}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default SupplyAuthorizationsList
